import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PagingConfig } from '../../../shared/PreWorkServiceTypes';
import { useSnapshot } from 'valtio';
import {
	CandidatesBatchState,
	MentorsListState,
} from '../CreateGroupPage/CandidatesList.store';
import {
	COUNTRY_CODE_INDIA,
	DEFAULT_PAGE_SIZE,
} from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { assignPreworkMentor } from '../../../service/PreWorkService';
import { useConfirmationBeforeAction } from '../../../hooks/useConfirmationBeforeAction';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { useCallApi } from '../../../hooks/useCallApi';
import { usePreworkGroupId } from '../../../hooks/usePreworkGroupId';
import { useEditGroupPreWork } from '../EditGroupPage/useEditGroup';
import {
	GetPreceptorsResponse,
	PreceptorsFilter,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { getPreceptors } from '../../../service/VolunteerApplicationService';
import useUserSession from '../../../hooks/useUserSession';

export const useAssignMentorPrework = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { callApi } = useCallApi();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const { confirmBeforeAction } = useConfirmationBeforeAction();
	const { preworkGroupId } = usePreworkGroupId();
	const { group } = useEditGroupPreWork();
	const [mentorsList, setMentorsList] = useState<GetPreceptorsResponse>({
		preceptors: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const mentorsFilterStateSnap = useSnapshot(MentorsListState);
	const [mentorPaging, setMentorPaging] = useState<PagingConfig>(() => {
		return { ...mentorsFilterStateSnap.paging } as PagingConfig;
	});
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);
	const batchIdInSnap = candidateBatchSnap.selectedBatch?.id;
	const [mentorsFilter, setMentorsFilter] = useState<PreceptorsFilter>({
		showMentors: true,
		countryIds: [COUNTRY_CODE_INDIA],
		batchId: batchIdInSnap,
		isPrework: true,
		excludeIds: [],
	});

	useEffect(() => {
		const excludeIds: Array<number> = [];
		if (group && group?.mentor) {
			excludeIds.push(Number(group.mentor.id));
		}
		setMentorsFilter((mentorFilter) => ({ ...mentorFilter, excludeIds }));
	}, [group]);

	const onConfirmAssignMentor = useCallback(
		async (mentorId: number) => {
			try {
				await callApi(
					() =>
						assignPreworkMentor({
							functionarySrcmId: srcmId as string,
							preworkGroupId,
							mentorId,
						}),
					'errorOccurredWhileAssigningMentor'
				);
				Modal.success({
					content: t('assignedMentorSuccessfully'),
					afterClose: () => {
						navigate(-1);
					},
				});
			} catch (e) {
				// Do Nothing
			}
		},
		[callApi, srcmId, t, preworkGroupId, navigate]
	);

	const onAssignMentorClick = useCallback(
		(mentorId: number) => {
			confirmBeforeAction(
				'confirmAssignMentor',
				'areYouSureToAssignMentorPrework',
				() => onConfirmAssignMentor(mentorId)
			);
		},
		[confirmBeforeAction, onConfirmAssignMentor]
	);

	const onMentorsPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			MentorsListState.paging = { pageNo: pageNumber, pageSize };
			setMentorPaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const onMentorFilterChange = useCallback(
		(filterParam: PreceptorsFilter) => {
			MentorsListState.filterBy = { ...filterParam };
			setMentorsFilter(filterParam);
			onMentorsPageChange(1, mentorPaging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onMentorsPageChange, mentorPaging.pageSize]
	);

	const fetchMentorsList = useCallback(
		async (pageNumber: number, pageSize: number, filter: PreceptorsFilter) => {
			if (srcmId && selectedRole) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const mentorsWithoutKey = await getPreceptors({
						functionarySrcmId: srcmId,
						selectedRoleCode: selectedRole,
						pageIndex,
						pageSize,
						preceptorsFilter: filter,
					});
					const mentors = {
						...mentorsWithoutKey,
						...(mentorsWithoutKey.preceptors && {
							preceptors: mentorsWithoutKey.preceptors
								.filter((mentor) => mentor.prefectStatus === 'ACTIVE')
								.map((mentor) => ({
									...mentor,
									key: mentor.id,
								})),
						}),
					};

					if (
						mentors.pagination.totalNoOfPages &&
						mentors.pagination.totalNoOfPages > 0 &&
						pageNumber > mentors.pagination.totalNoOfPages
					) {
						onMentorsPageChange(
							mentors.pagination.totalNoOfPages ?? 1,
							pageSize
						);
					} else {
						setMentorsList(mentors);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, selectedRole, setLoading, onMentorsPageChange, showToastMessage]
	);

	useEffect(() => {
		void fetchMentorsList(
			mentorPaging.pageNo ?? 1,
			mentorPaging.pageSize ?? DEFAULT_PAGE_SIZE,
			mentorsFilter
		);
	}, [mentorPaging, mentorsFilter, fetchMentorsList]);

	return {
		mentorsList,
		mentorsFilter,
		onMentorFilterChange,
		onMentorsPageChange,
		mentorPaging,
		onAssignMentorClick,
	};
};
